import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';

import DeliveryPerson from '../images/delivery-person.png';
import EndOfDay from '../images/end-of-day-illustration.png';
import Percentage from '../images/percentage.png';
import ShoppingBasket from '../images/shopping-basket.png';
import Smiley from '../images/smiley.png';
import { CampaignCreationSource, CampaignOption } from '../types';

type CampaignConfigEntry = Omit<CampaignOption, 'tag' | 'title' | 'description'> & {
  tagId?: string;
  titleId: string;
  descriptionId: string;
};

const campaignOptionsConfig: CampaignConfigEntry[] = [
  {
    type: CampaignType.DELIVERY_DISCOUNT,
    titleId: 'campaigns.free-delivery.title',
    descriptionId: 'campaigns.free-delivery.body',
    image: DeliveryPerson,
  },
  {
    type: CampaignType.BASKET_DISCOUNT,
    tagId: 'campaigns.new',
    titleId: 'campaigns.basket-discount.title',
    descriptionId: 'campaigns.basket-discount.body',
    image: ShoppingBasket,
  },
  {
    type: CampaignType.ITEM_DISCOUNT,
    tagId: 'campaigns.new',
    titleId: 'campaigns.item-discount.title',
    descriptionId: 'campaigns.item-discount.body',
    image: Percentage,
  },
  {
    type: CampaignType.END_OF_DAY,
    tagId: 'campaigns.new',
    titleId: 'campaigns.end-of-day.title',
    descriptionId: 'campaigns.end-of-day.body',
    image: EndOfDay,
  },
  {
    type: CampaignType.ADS,
    tagId: 'campaigns.new',
    titleId: 'campaigns.ads.title',
    descriptionId: 'campaigns.ads.body',
    image: Smiley,
  },
];

const useCampaignsOptions = () => {
  const intl = useIntl();

  const campaigns: CampaignOption[] = useMemo(
    () =>
      campaignOptionsConfig.map(({ descriptionId, tagId, titleId, type, ...campaign }) => ({
        ...campaign,
        tag: tagId ? intl.formatMessage({ id: tagId }) : undefined,
        title: intl.formatMessage({ id: titleId }),
        description: intl.formatMessage({ id: descriptionId }),
        type,
      })),
    [intl],
  );

  const getCampaignTitleByType = (campaignType?: CampaignType, creationSource?: CampaignCreationSource) => {
    return campaignType === CampaignType.ADS && creationSource === 'opsTools'
      ? intl.formatMessage({ id: 'campaigns.ads.title-managed-ad-campaign' })
      : campaigns.find(campaign => campaign.type === campaignType)?.title || '';
  };

  return { campaigns, getCampaignTitleByType };
};

export default useCampaignsOptions;
